import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUrl, setUserDetails } from "../Redux/action";
import tw from "tailwind-styled-components";
import Onboarding from "../Components/Checkout/Onboarding";
import Footer from "../Components/Checkout/Footer";
import Header from "../Components/Checkout/Header";
import { useQuery } from "react-query";
import axios from "axios";
import Config from "../Config";
import NotFoundModel from "../Components/NotFoundModel";
import SkeletonLoader from "../Components/SkeletonLoader";
import { Sheet } from "react-modal-sheet";
import { toast } from "react-toastify";
import { Images } from "../Images";
import { load } from "@cashfreepayments/cashfree-js";

// Import new components
import PendingDuesList from "../Components/PendingDuesList";
import PaymentSheet from "../Components/PaymentSheet";
import { PayButton, PayOfflineButton } from "../Components/StyledComponents";
import PaymentSuccessBox from "../Components/PaymentSuccessBox";
import { usePaymentMutations } from "../hooks/usePaymentMutations";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { useDueSelection } from "../hooks/useDueSelection";
import { useChargesCalculation } from "../hooks/useChargesCalculation";
import { useCashfreePayment } from "../hooks/useCashfreePayment";
import { formatDateRange, duesLabelMapping } from "../utils/paymentUtils";
import CollectorsSheet from "../Components/CollectorSheet";
import { useCollectors } from "../hooks/useCollectors";

const BgColor = tw.div`flex justify-center items-center bg-white`;
const PageWrapper = tw.div`flex flex-col w-full mx-auto md:max-w-sm bg-zinc-50 min-h-screen`;
const FooterWrapper = tw.div`w-full bg-zinc-50 py-3`;

const MakePayment = () => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [userEmail, setUserEmail] = useState("");
  const [charges, setCharges] = useState("0");
  const [totalPayment, setTotalPayment] = useState("");
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("");
  const [availableMethods, setAvailableMethods] = useState([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [gateway, setGateway] = useState(1);
  const [totalDuesAmount, setTotalDuesAmount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [emailRequired, setEmailRequired] = useState(true);
  const shouldFetchChargesRef = useRef(false);
  const clickedPaymentMethodRef = useRef("");
  const [tenantData, setTenantData] = useState("");
  const gId = useSelector((state) => state.UserReducer.gId);
  const dueId = useSelector((state) => state.UserReducer.dueId);
  const token = useSelector(
    (state) => state.UserReducer.userDetails?.data?.data?.token,
  );
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [offlinePaymentEnabled, setOfflinePaymentEnabled] = useState(false);
  const [isCollectorsSheetOpen, setIsCollectorsSheetOpen] = useState(false);

  // Custom hooks
  const {
    selectedDues,
    selectedDuesTotal,
    hasSelectedDue,
    allSelected,
    setAllSelected,
    handleDueSelection,
    initializeSelectedDues,
  } = useDueSelection();

  const {
    processPaymentMethods,
    getPaymentGateway,
    getIsLoading,
    renderPaymentMethodLabel,
    renderPaymentMethodDescription,
    renderPaymentMethodIcon,
  } = usePaymentMethods(gateway);

  const {
    fetchChargesForSelectedMethod,
    chargesTransactionLoading,
    chargesTransactionMutate,
  } = useChargesCalculation(token, selectedDuesTotal, totalDuesAmount, gstAmount, setCharges, setTotalPayment);

  const {
    handleCashfreePayment,
    isCashfreeInitializing,
  } = useCashfreePayment(token, userEmail, selectedPaymentOption, charges, gstAmount, selectedDues, selectedDuesTotal, totalDuesAmount, tenantData);

  const {
    payUTransactionMutate,
    easebuzzTransactionMutate,
    isPayUTransactionLoading,
    isEasebuzzTransactionLoading,
  } = usePaymentMutations(token);

  const {
    collectors,
    isLoadingCollectors,
    collectorsError,
    refetchCollectors,
  } = useCollectors(token, isCollectorsSheetOpen);

  // Fetch tenant details
  const fetchTenantDetails = async () => {
    if (!gId) return null;
    return await axios.get(
      `${Config.ApiUrl}/tenant/web/payment/${gId}/${dueId || undefined}`,
    );
  };

  const {
    data,
    error,
    refetch,
    isLoading: isInitialLoading,
  } = useQuery("getTenantDetails", fetchTenantDetails, {
    enabled: !!gId,
    onSuccess: (data) => {
      dispatch(setUserDetails(data));
      const gatewayValue = data?.data?.data?.gateway || 1;
      setGateway(gatewayValue);
      const { filtered, defaultMethod } = processPaymentMethods(data);
      setAvailableMethods(filtered);
      setTenantData(data?.data?.data);
      setSelectedPaymentOption(defaultMethod);
      clickedPaymentMethodRef.current = defaultMethod;
      setPaymentEnabled(data?.data?.data?.paymentEnabled);
      setOfflinePaymentEnabled(data?.data?.data?.offlineEnabled);
      const totalDues = data?.data?.data?.totalDues || 0;
      const gstAmountValue = data?.data?.data?.gstAmount || 0;

      setTotalDuesAmount(totalDues);
      setGstAmount(gstAmountValue);
      setEmailRequired(data?.data?.data?.emailRequired);

      initializeSelectedDues(data, (selectedTotal) => {
        const defaultMethodObj = filtered.find(m => m.type === defaultMethod);
        const defaultCharges = defaultMethodObj?.initialCharges || "0";
        setCharges(defaultCharges);

        const total = (
          parseFloat(selectedTotal) +
          parseFloat(defaultCharges || 0) +
          parseFloat(gstAmountValue)
        ).toFixed(2);
        setTotalPayment(total);
      });

      shouldFetchChargesRef.current = true;
    },
  });

  const isBookingPayment = data?.data?.data?.isBookingPayment;
  const enableDueSelection = data?.data?.data?.enableDueSelection;

  useEffect(() => {
    dispatch(setUrl(window.location.href));
  }, [dispatch]);

  useEffect(() => {
    if (gId) refetch();
  }, [gId, refetch]);

  useEffect(() => {
    if (step === 3 && selectedPaymentOption) {
      if (shouldFetchChargesRef.current) {
        const amountToPay = isBookingPayment ? totalDuesAmount : selectedDuesTotal;
        fetchChargesForSelectedMethod(selectedPaymentOption, amountToPay, availableMethods);
        shouldFetchChargesRef.current = false;
      } else if (clickedPaymentMethodRef.current !== selectedPaymentOption) {
        const amountToPay = isBookingPayment ? totalDuesAmount : selectedDuesTotal;
        fetchChargesForSelectedMethod(selectedPaymentOption, amountToPay, availableMethods);
        clickedPaymentMethodRef.current = selectedPaymentOption;
      }
    }
  }, [step, selectedPaymentOption, selectedDuesTotal]);

  const handlePaymentOptionClick = (type) => {
    if (type === selectedPaymentOption) return;
    setSelectedPaymentOption(type);
  };

  const handlePayNow = () => {
    let selectedDueItems;
    let amountToPay;

    if (enableDueSelection) {
      selectedDueItems = selectedDues.filter((due) => due.selected);
      if (selectedDueItems.length === 0) {
        toast.error("Please select at least one due");
        return;
      }
      amountToPay = isBookingPayment ? totalDuesAmount : selectedDuesTotal;
    } else {
      selectedDueItems = selectedDues;
      amountToPay = totalDuesAmount;
    }

    const payload = {
      email: userEmail,
      mode: selectedPaymentOption,
      dueId: selectedDueItems.map((due) => due.id),
      ledgerReferenceId: selectedDueItems.map((due) => due.ledgerReferenceId),
      amount: amountToPay,
      charges: charges,
      gstAmount: gstAmount,
      isBookingPayment,
    };

    const selectedGateway = getPaymentGateway(selectedPaymentOption);
    if (selectedGateway === Config.PAYMENT_GATEWAY.EASEBUZZ) {
      easebuzzTransactionMutate(payload);
    } else if (selectedGateway === Config.PAYMENT_GATEWAY.CASHFREE) {
      handleCashfreePayment(payload);
    } else {
      payUTransactionMutate(payload);
    }
  };

  const handleNextToStep2 = (isOffline = false) => {
    if (enableDueSelection && !hasSelectedDue) {
      toast.error("Please select at least one due");
      return;
    }

    if (isOffline) {
      setIsCollectorsSheetOpen(true);
      return;
    }

    // Online payment flow
    if (emailRequired === false) {
      setStep(3);
      setIsSheetOpen(true);
    } else {
      setStep(2);
      setIsSheetOpen(true);
    }
  };

  const handleCashPayment = () => {
    if (enableDueSelection && !hasSelectedDue) {
      toast.error("Please select at least one due");
      return;
    }
    setIsCollectorsSheetOpen(true);
  };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setStep(1);
    shouldFetchChargesRef.current = true;
  };

  const handleCollectorsSheetClose = () => {
    setIsCollectorsSheetOpen(false);
  };

  const getCurrentTarget = (e) => {
    const targetClass = Array.from(e.target.classList);
    if (targetClass.includes("addCommentModel")) {
      if (isSheetOpen) {
        handleSheetClose();
      }
      if (isCollectorsSheetOpen) {
        handleCollectorsSheetClose();
      }
    }
  };

  if (error) return <NotFoundModel />;

  return (
    <>
      <BgColor>
        <PageWrapper>
          {isInitialLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              <Header HeaderData={data?.data?.data} />
              <div className="flex-grow">
                <div className="relative">
                  <Onboarding RoomData={data?.data?.data?.occupancy} />
                </div>
                {step === 1 && (
                  <div className="relative">
                    {totalDuesAmount !== 0 ? (
                      <PendingDuesList
                        selectedDues={selectedDues}
                        totalDuesAmount={totalDuesAmount}
                        isBookingPayment={isBookingPayment}
                        enableDueSelection={enableDueSelection}
                        onDueSelection={handleDueSelection}
                        formatDateRange={formatDateRange}
                        duesLabelMapping={duesLabelMapping}
                      />
                    ) : (
                      <PaymentSuccessBox image={Images.SuccessGif} />
                    )}
                  </div>
                )}
              </div>
              {totalDuesAmount !== 0 && (
                <div className="flex justify-between items-center mx-6 gap-4">
                  {offlinePaymentEnabled &&
                    <PayOfflineButton
                      onClick={handleCashPayment}
                      disabled={(enableDueSelection ? !hasSelectedDue : false)}
                    >
                      Pay via Cash
                    </PayOfflineButton>}
                  {paymentEnabled &&
                    <PayButton
                      onClick={() => handleNextToStep2(false)}
                      disabled={(enableDueSelection ? !hasSelectedDue : false) || !paymentEnabled}
                      className={(enableDueSelection ? !hasSelectedDue : false) || !paymentEnabled ? "opacity-70 cursor-not-allowed" : ""}
                    >
                      Pay Online
                    </PayButton>
                  }
                </div>
              )}
              <FooterWrapper>
                <Footer />
              </FooterWrapper>
            </>
          )}
        </PageWrapper>
      </BgColor>

      {/* Separate overlays for each sheet */}
      {isSheetOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900 bg-opacity-70 backdrop-blur-sm transition-opacity addCommentModel"
          onClick={getCurrentTarget}
        />
      )}

      {isCollectorsSheetOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-900 bg-opacity-70 backdrop-blur-sm transition-opacity addCommentModel"
          onClick={getCurrentTarget}
        />
      )}

      <PaymentSheet
        isOpen={isSheetOpen}
        onClose={handleSheetClose}
        step={step}
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        setStep={setStep}
        selectedPaymentOption={selectedPaymentOption}
        availableMethods={availableMethods}
        charges={charges}
        totalPayment={totalPayment}
        totalDuesAmount={totalDuesAmount}
        selectedDuesTotal={selectedDuesTotal}
        gstAmount={gstAmount}
        gateway={gateway}
        isBookingPayment={isBookingPayment}
        enableDueSelection={enableDueSelection}
        chargesTransactionLoading={chargesTransactionLoading}
        isProcessing={getIsLoading(selectedPaymentOption) || isCashfreeInitializing}
        onPaymentOptionClick={handlePaymentOptionClick}
        onPayNow={handlePayNow}
        onFetchCharges={() => {
          const amountToPay = isBookingPayment ? totalDuesAmount : selectedDuesTotal;
          fetchChargesForSelectedMethod(selectedPaymentOption, amountToPay, availableMethods);
        }}
        renderPaymentMethodIcon={renderPaymentMethodIcon}
        renderPaymentMethodLabel={renderPaymentMethodLabel}
        renderPaymentMethodDescription={renderPaymentMethodDescription}
      />

      <CollectorsSheet
        isOpen={isCollectorsSheetOpen}
        onClose={handleCollectorsSheetClose}
        collectors={collectors}
        isLoading={isLoadingCollectors}
        onRetry={refetchCollectors}
        selectedDues={selectedDues}
        totalAmount={enableDueSelection ? selectedDuesTotal : totalDuesAmount}
        enableDueSelection={enableDueSelection}
        token={token}
      />
    </>
  );
};

export default MakePayment;